import { siteConfig } from "../config/siteConfig";
import { PageHero } from "./PageHero";
import { Separator } from "./ui/separator";

interface TermsOfServicePageProps {
  onNavigate: (page: string) => void;
}

export function TermsOfServicePage({ onNavigate }: TermsOfServicePageProps) {
  return (
    <div className="min-h-screen bg-white">
      <PageHero
        title="Terms of Service"
        lede={<><p>Welcome to TurfAI (the "Platform"), operated by TurfAI ("we", "our", "us"). By accessing or using our website (https://turfai.in) and our document processing services, you agree to be bound by these Terms of Service.</p><p className="mt-3 text-[15px] font-semibold text-soft-black">Effective Date: 21 January 2026</p></>}
      />


      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-12">

            <div>
              <h2 className="text-[24px] lg:text-[28px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">1. Acceptance of Terms</h2>
              <p className="text-soft-black">
                By creating an account or using our services, you confirm that you can form a binding contract with TurfAI and that you comply with all applicable local and international laws.
              </p>
            </div>

            <div>
              <h2 className="text-[24px] lg:text-[28px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">2. Description of Service</h2>
              <p className="text-soft-black">
                TurfAI provides AI-driven document processing, workflow automation, and integration with third-party services (such as Google Gmail and Google Drive). We reserve the right to modify, suspend, or discontinue any feature of the service at any time without prior notice.
              </p>
            </div>

            <div>
              <h2 className="text-[24px] lg:text-[28px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">3. User accounts and security</h2>
              <ul className="space-y-3 text-soft-black">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                  <span><strong>Account Responsibility:</strong> You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                  <span><strong>OAuth Integration:</strong> When you connect your Google Account, you grant TurfAI permission to access your data as specified during the consent process. You may revoke this access at any time via your Google Security settings.</span>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-[24px] lg:text-[28px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">4. Acceptable use policy</h2>
              <p className="text-soft-black mb-4">You agree <strong>not</strong> to use TurfAI for:</p>
              <ul className="space-y-3 text-soft-black">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                  <span>Any illegal or unauthorised purpose.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                  <span>Processing sensitive personal data (e.g., health or financial records) in a way that violates privacy laws (GDPR, CCPA, etc.).</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                  <span>Attempting to reverse engineer the AI models or bypass service limitations.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                  <span>Sending spam or unauthorised automated communications via our Gmail integration.</span>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-[24px] lg:text-[28px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">5. AI content and accuracy</h2>
              <ul className="space-y-3 text-soft-black">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                  <span><strong>AI Outputs:</strong> You acknowledge that the outputs generated by AI (summaries, data extraction, etc.) may occasionally be inaccurate or incomplete.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                  <span><strong>Human Verification:</strong> Users are responsible for reviewing and verifying the accuracy of any AI-generated output before relying on it for business or legal purposes.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                  <span><strong>No Liability:</strong> TurfAI is not liable for damages resulting from errors or "hallucinations" in AI-generated content.</span>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-[24px] lg:text-[28px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">6. Intellectual property</h2>
              <ul className="space-y-3 text-soft-black">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                  <span><strong>Our Content:</strong> The TurfAI platform, including its software, branding, and proprietary algorithms, is the property of TurfAI.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                  <span><strong>Your Data:</strong> You retain all ownership rights to the data you process through our service. By using the service, you grant us a limited license to process your data solely for the purpose of fulfilling your requested workflows.</span>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-[24px] lg:text-[28px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">7. Limitation of Liability</h2>
              <p className="text-soft-black">
                To the maximum extent permitted by law, TurfAI shall not be liable for any indirect, incidental, special, or consequential damages, including loss of profits or data, arising out of your use of the service. Our total liability shall not exceed the amount paid by you to us in the last 12 months.
              </p>
            </div>

            <div>
              <h2 className="text-[24px] lg:text-[28px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">8. Termination</h2>
              <p className="text-soft-black">
                We reserve the right to terminate or suspend your access to the service immediately, without prior notice, for conduct that we believe violates these Terms or is harmful to other users or our business interests.
              </p>
            </div>

            <div>
              <h2 className="text-[24px] lg:text-[28px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">9. Changes to Terms</h2>
              <p className="text-soft-black">
                We may update these Terms from time to time. We will notify you of any changes by posting the new Terms on this page and updating the "Effective Date." Continued use of the service after changes signifies your acceptance.
              </p>
            </div>

            <div>
              <h2 className="text-[24px] lg:text-[28px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">10. Governing law</h2>
              <p className="text-soft-black">
                These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions.
              </p>
            </div>

            <div>
              <h2 className="text-[24px] lg:text-[28px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">11. Contact information</h2>
              <p className="text-soft-black">
                For any questions regarding these Terms, please contact:
              </p>
              <p className="text-soft-black mt-2">
                Email: <a href={`mailto:${siteConfig.contact.general}`} className="prose-link">{siteConfig.contact.general}</a>
              </p>
              <p className="text-soft-black">
                Website: <a href="https://turfai.in" className="prose-link">https://turfai.in</a>
              </p>
            </div>

            <Separator className="my-8" />

            <div className="text-center">
              <p className="text-sm text-soft-black mb-4">
                Questions about these terms?
              </p>
              <p className="text-sm text-soft-black">
                Contact us at <a href={`mailto:${siteConfig.contact.general}`} className="prose-link">{siteConfig.contact.general}</a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
